<template>
  <div class="game-frame">
    <!-- Top toolbar -->
    <div class="toolbar">
      <button class="back-btn" @click="router.back()">Back</button>
      <h2>{{ game?.title }}</h2>
      <button class="fullscreen-btn" @click="toggleFullscreen">
        {{ isFullscreen ? 'Exit Fullscreen' : 'Fullscreen' }}
      </button>
    </div>

    <!-- Game content area -->
    <div class="game-container" ref="gameContainer">
      <iframe 
        v-if="game?.url"
        :src="game.url"
        frameborder="0"
        allowfullscreen
      ></iframe>
      <div v-else class="game-placeholder">
        <p>Loading game...</p>
      </div>
    </div>

    <!-- Bottom info bar -->
    <div class="info-bar">
      <div class="controls">
        <button @click="showInstructions = !showInstructions">
          {{ showInstructions ? 'Hide Instructions' : 'Show Instructions' }}
        </button>
      </div>
      
      <div v-if="showInstructions" class="instructions">
        <h3>Game Instructions</h3>
        <p>{{ game?.instructions }}</p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useGameStore } from '../stores/gameStore'

const route = useRoute()
const router = useRouter()
const store = useGameStore()

const game = ref(null)
const gameContainer = ref(null)
const isFullscreen = ref(false)
const showInstructions = ref(false)

// 从 itch.io URL 中提取游戏 ID
const getItchGameId = (url) => {
  if (!url) return ''
  const match = url.match(/itch\.io\/([^\/]+)$/)
  return match ? match[1] : ''
}

onMounted(async () => {
  const gameId = parseInt(route.params.id)
  // 先尝试从 store 中获取游戏数据
  game.value = store.getGameById(gameId)
  
  // 如果 store 中没有数据，则加载数据
  if (!game.value) {
    await store.loadGames()
    game.value = store.getGameById(gameId)
  }
  
  // 监听全屏变化
  document.addEventListener('fullscreenchange', handleFullscreenChange)
  document.addEventListener('webkitfullscreenchange', handleFullscreenChange)
  document.addEventListener('mozfullscreenchange', handleFullscreenChange)
  document.addEventListener('MSFullscreenChange', handleFullscreenChange)
})

onUnmounted(() => {
  document.removeEventListener('fullscreenchange', handleFullscreenChange)
  document.removeEventListener('webkitfullscreenchange', handleFullscreenChange)
  document.removeEventListener('mozfullscreenchange', handleFullscreenChange)
  document.removeEventListener('MSFullscreenChange', handleFullscreenChange)
})

const handleFullscreenChange = () => {
  isFullscreen.value = !!(
    document.fullscreenElement ||
    document.webkitFullscreenElement ||
    document.mozFullScreenElement ||
    document.msFullscreenElement
  )
}

const toggleFullscreen = () => {
  if (!isFullscreen.value) {
    if (gameContainer.value.requestFullscreen) {
      gameContainer.value.requestFullscreen()
    } else if (gameContainer.value.webkitRequestFullscreen) {
      gameContainer.value.webkitRequestFullscreen()
    } else if (gameContainer.value.mozRequestFullScreen) {
      gameContainer.value.mozRequestFullScreen()
    } else if (gameContainer.value.msRequestFullscreen) {
      gameContainer.value.msRequestFullscreen()
    }
  } else {
    if (document.exitFullscreen) {
      document.exitFullscreen()
    } else if (document.webkitExitFullscreen) {
      document.webkitExitFullscreen()
    } else if (document.mozCancelFullScreen) {
      document.mozCancelFullScreen()
    } else if (document.msExitFullscreen) {
      document.msExitFullscreen()
    }
  }
}
</script>

<style scoped>
.game-frame {
  display: flex;
  flex-direction: column;
  height: 100vh;
  background: #1a1a1a;
  color: white;
}

.toolbar {
  display: flex;
  align-items: center;
  padding: 10px 20px;
  background: #2a2a2a;
  box-shadow: 0 2px 4px rgba(0,0,0,0.2);
}

.toolbar h2 {
  flex: 1;
  margin: 0;
  text-align: center;
}

.back-btn, .fullscreen-btn {
  padding: 8px 16px;
  background: #1E90FF;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.back-btn:hover, .fullscreen-btn:hover {
  background: #1873CC;
}

.game-container {
  flex: 1;
  position: relative;
  overflow: hidden;
}

.game-container iframe {
  width: 100%;
  height: 100%;
  border: none;
}

.game-placeholder {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  color: #666;
}

.info-bar {
  padding: 15px 20px;
  background: #2a2a2a;
  border-top: 1px solid #3a3a3a;
}

.controls {
  display: flex;
  justify-content: center;
  margin-bottom: 10px;
}

.controls button {
  padding: 8px 16px;
  background: #9370DB;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.controls button:hover {
  background: #7B68EE;
}

.instructions {
  background: #333;
  padding: 15px;
  border-radius: 4px;
  margin-top: 10px;
}

.instructions h3 {
  margin: 0 0 10px 0;
  color: #1E90FF;
}

.instructions p {
  margin: 0;
  line-height: 1.5;
  color: #ccc;
}
</style> 